import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'components/commercetools-ui/atoms/link';
import useClassNames from 'helpers/hooks/useClassNames';
import { usePageLoader } from 'providers/loader';
import { useProductList } from '../../context';

interface Props {
  query: string;
  productLength?: number;
  textConfiguration?: any[];
}

const SearchHeader: React.FC<Props> = ({ query, productLength, textConfiguration }) => {
  const {
    resultsForHeading,
    resultsForSubHeading1,
    resultsForSubHeading2,
    noResultsForHeading,
    noResultsForSubHeading,
    noResultsTipsHeading,
    links,
    noResultsLinksHeading,
    tips,
  } = textConfiguration?.[0] ?? {};
  const { isLoading } = usePageLoader();
  const { totalItems } = useProductList();
  const containerClassName = useClassNames(['flex flex-col', totalItems ? 'gap-4' : '']);
  const searchParams = useSearchParams();
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const searchResultHeading = totalItems
    ? `${totalItems} ${resultsForHeading} ${query}`
    : `${noResultsForHeading} ${query}`;
  const searchResultSubHeading = totalItems
    ? `${resultsForSubHeading1} 1 - ${productLength} ${resultsForSubHeading2} ${totalItems}`
    : `${noResultsForSubHeading} ${query}`;

  useEffect(() => {
    if (!query || isLoading) {
      setLiveAnnouncement('');
      return;
    }
    setLiveAnnouncement('');
    // Update the live region after render so screen readers announce it reliably.
    const timer = globalThis.setTimeout(() => {
      const announcement = searchResultHeading + `. \n` + searchResultSubHeading;
      setLiveAnnouncement(announcement);
    }, 100);

    return () => globalThis.clearTimeout(timer);
  }, [query, totalItems, isLoading, searchResultHeading, searchResultSubHeading]);

  if (!query) return null;

  return (
    <div className={containerClassName}>
      <div id="search-status" role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {liveAnnouncement}
      </div>
      <div>
        <div>
          <h2
            className={`${totalItems ? 'text-16 font-bold leading-5 md:font-normal' : 'text-28 font-normal leading-9'} ${totalItems ? 'lg:text-28 lg:leading-[36px]' : 'lg:text-40 lg:leading-[48px]'}`}
            data-ffid="lbl-search-results"
          >
            {searchResultHeading}
          </h2>
        </div>
        <div className={totalItems ? 'pb-16 lg:pb-0' : 'my-24 lg:my-0'}>
          <p
            id="search-results-message"
            className="text-18 leading-6 text-[#5C5C5C] lg:text-28 lg:leading-9"
            data-ffid="lbl-search-results-count"
          >
            {searchResultSubHeading}
          </p>
        </div>
        {!totalItems && !searchParams.get('wishListIds[0]') && (
          <div className="flex flex-col gap-28 lg:mt-24">
            <div className="flex flex-col">
              <h4 className="text-20" data-ffid="lbl-noresult-tips">
                {noResultsTipsHeading}
              </h4>
              <ul className="flex list-inside list-disc flex-col pl-8" data-ffid="lst-no-search-results-tips">
                {tips?.map((item: any, index: any) => (
                  <li key={item?.tip} className="text-20 leading-7" data-ffid={`lst-no-search-results-tip-${index}`}>
                    <span data-ffid={`lbl-no-search-results-tip-${index}`}>{item?.tip}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col">
              <h4 className="text-20 leading-9" data-ffid="lbl-noresult-head">
                {noResultsLinksHeading}
              </h4>
              <ul className="flex list-inside list-disc flex-col pl-8" data-ffid="lst-no-search-results-Links">
                {links?.map((item: any, index: any) => (
                  <li
                    key={item?.linkText}
                    className="text-20 leading-7 text-[#0B57D0]"
                    data-ffid={`lst-no-search-results-link-${index}`}
                  >
                    <Link link={item?.linkReference} data-ffid={`lnk-no-search-results-link-${index}`}>
                      <span data-ffid={`lbl-no-search-results-link-${index}`}>{item?.linkText}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchHeader;
